/**
 * WalkinFormDialog
 *
 * Quick "log a walk-in" form launched from the homepage. Accessible to
 * every logged-in user (staff, creator, dispatcher, manager, admin) —
 * unlike the admin-only listing/edit views. Only `customer_name` is
 * mandatory; the rest are optional so the form takes 10-15 seconds to
 * fill on the showroom floor.
 *
 * On follow-up date, the backend reminder service (every 10 min) will
 * WhatsApp this entry to the salesperson AND all admins.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { api, useAuth, todayIst, addDaysIst } from '../../lib/store';
import { toast } from 'sonner';
import { Loader2, Users, X } from 'lucide-react';

const todayIstLocal = todayIst;
const followUpDefaultIst = () => addDaysIst(3);

const initial = {
  customer_name: '',
  customer_phone: '',
  location: '',
  // Pre-fill visit_date with today (IST). Staff can change it to a past
  // date if they're back-filling a walk-in they forgot to register.
  visit_date: todayIstLocal(),
  products_shown: '',
  architect_name: '',
  carpenter_name: '',
  follow_up_date: '',
  notes: '',
};

// Helper to build a fresh form state with today's IST date — used both
// when the dialog first mounts AND whenever it is re-opened, so a staff
// opening the dialog at 12:05 AM gets the new day's date.
const buildInitial = () => ({
  ...initial,
  visit_date: todayIstLocal(),
  // Follow-up defaults to +3d for NEW entries. Blank = no follow-up.
  follow_up_date: followUpDefaultIst(),
});

export default function WalkinFormDialog({ open, onOpenChange, onCreated, editRecord, onUpdated }) {
  const isEdit = !!editRecord;
  // Build the starting form state — either a fresh create form or a
  // full clone of the record we were handed. Kept memo-free (called
  // only on mount + on-open resets) so the two paths stay obvious.
  const buildFromRecord = () => ({
    customer_name:   editRecord?.customer_name   || '',
    customer_phone:  editRecord?.customer_phone  || '',
    location:        editRecord?.location        || '',
    visit_date:      editRecord?.visit_date      || todayIstLocal(),
    products_shown:  editRecord?.products_shown  || '',
    architect_name:  editRecord?.architect_name  || '',
    carpenter_name:  editRecord?.carpenter_name  || '',
    // NEVER auto-inject the +3d default in edit mode — the
    // stored value (blank or otherwise) must survive the round trip.
    follow_up_date:  editRecord?.follow_up_date  || '',
    notes:           editRecord?.notes           || '',
  });
  const [form, setForm] = useState(isEdit ? buildFromRecord() : buildInitial());
  const [submitting, setSubmitting] = useState(false);
  const { user: currentUser } = useAuth();

  // List of all staff (incl. self) for the "Attended By" multi-select.
  // Fetched once when the dialog first opens so we don't burn an API
  // call on every keystroke.
  const [allStaff, setAllStaff] = useState([]);
  const [staffQuery, setStaffQuery] = useState('');
  const [showStaffDropdown, setShowStaffDropdown] = useState(false);
  // attendingStaff = [{user_id, name, phone, role}] — array shape lets
  // the backend dedupe + fan-out WhatsApp reminders to every entry.
  const [attendingStaff, setAttendingStaff] = useState([]);

  // ─── Repeat-customer autofill ────────────────────────────────────
  // When the customer_name input has 2+ chars, hit GET /walkins/suggest
  // (debounced 300ms) and offer the recent matches. Clicking one
  // fills name/phone/location. Typing anything else works as before.
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [pickedName, setPickedName] = useState('');
  useEffect(() => {
    if (!open) return;
    const q = form.customer_name.trim();
    // Don't re-suggest after the user picked from the list (until they
    // start editing again).
    if (q.length < 2 || q === pickedName) {
      setNameSuggestions([]);
      return;
    }
    const t = setTimeout(() => {
      api.suggestWalkins(q)
        .then((res) => {
          setNameSuggestions(res.data?.suggestions || []);
          setShowNameSuggestions(true);
        })
        .catch(() => setNameSuggestions([]));
    }, 300);
    return () => clearTimeout(t);
  }, [form.customer_name, open, pickedName]);

  const applySuggestion = (s) => {
    setForm((f) => ({
      ...f,
      customer_name:  s.name || f.customer_name,
      customer_phone: s.phone || f.customer_phone,
      location:       s.location || f.location,
    }));
    setPickedName(s.name || '');
    setShowNameSuggestions(false);
  };

  // Whenever the dialog opens, refresh visit_date + reset attending
  // staff to just the current user. This way the common case
  // ("only I attended this customer") stays a one-click save.
  // In edit mode, prefill from editRecord (never from the
  // fresh-today defaults) and seed attending_staff from the record.
  useEffect(() => {
    if (open) {
      if (isEdit) {
        setForm(buildFromRecord());
        setAttendingStaff(
          Array.isArray(editRecord?.attending_staff)
            ? editRecord.attending_staff.map((s) => ({
                user_id: s.user_id || s.id || '',
                name:    s.name    || '',
                phone:   s.phone   || '',
                role:    s.role    || '',
              }))
            : []
        );
      } else {
        setForm(buildInitial());
        if (currentUser?.id) {
          setAttendingStaff([{
            user_id: currentUser.id,
            name:    currentUser.name || '',
            phone:   currentUser.phone || '',
            role:    currentUser.role || '',
          }]);
        } else {
          setAttendingStaff([]);
        }
      }
      setStaffQuery('');
      setShowStaffDropdown(false);
    }
  }, [open, isEdit, editRecord?.id, currentUser?.id]);

  // Fetch staff list once (cached after the first open).
  useEffect(() => {
    if (!open || allStaff.length > 0) return;
    api.getSalespeople()
      .then((res) => setAllStaff(res.data?.salespeople || []))
      .catch(() => setAllStaff([]));
  }, [open, allStaff.length]);

  // Filter suggestions by typed query (case-insensitive name / staff_code match).
  // Exclude users already added to the attending list.
  const filteredStaff = useMemo(() => {
    const selectedIds = new Set(attendingStaff.map((s) => s.user_id));
    const q = staffQuery.trim().toLowerCase();
    return allStaff
      .filter((s) => !selectedIds.has(s.id))
      .filter((s) => !q ||
        (s.name || '').toLowerCase().includes(q) ||
        (s.staff_code || '').toLowerCase().includes(q))
      .slice(0, 10);
  }, [allStaff, attendingStaff, staffQuery]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addStaff = (s) => {
    if (!s?.id || attendingStaff.some((x) => x.user_id === s.id)) return;
    setAttendingStaff((arr) => [...arr, {
      user_id: s.id,
      name:    s.name || '',
      phone:   s.phone || '',
      role:    s.role || '',
    }]);
    setStaffQuery('');
    setShowStaffDropdown(false);
  };

  const removeStaff = (userId) => {
    setAttendingStaff((arr) => arr.filter((s) => s.user_id !== userId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, attending_staff: attendingStaff };
      if (isEdit) {
        // PUT the specific record. Backend permission is
        // now `get_current_user` (any authenticated user) so plain
        // staff hitting this from My Registrations works.
        const res = await api.updateWalkin(editRecord.id, payload);
        toast.success('Walk-in updated');
        onOpenChange(false);
        if (onUpdated) onUpdated(res.data);
      } else {
        const res = await api.createWalkin(payload);
        const fanout = attendingStaff.length;
        toast.success(fanout > 1
          ? `Walk-in saved — ${fanout} staff tagged for follow-up`
          : 'Walk-in saved');
        setForm(buildInitial());
        onOpenChange(false);
        if (onCreated) onCreated(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (isEdit ? 'Failed to update walk-in' : 'Failed to save walk-in'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] flex flex-col" data-testid="walkin-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#556B2F]" /> {isEdit ? 'Edit Walk-in' : 'Log a Walk-in Customer'}
          </DialogTitle>
          <p className="text-xs text-[#5C6150] mt-1">
            {isEdit
              ? 'Update any field and hit Save Changes. The follow-up date is left as-is unless you change it.'
              : 'Only customer name is required. Set a follow-up date to receive a WhatsApp reminder that day.'}
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto pr-1 space-y-3 [&_input]:min-h-11 sm:[&_input]:min-h-10">
          <div className="relative">
            <Label htmlFor="wf-name" className="text-xs">Customer Name <span className="text-red-500">*</span></Label>
            <Input
              id="wf-name"
              value={form.customer_name}
              onChange={(e) => { setPickedName(''); set('customer_name')(e); }}
              onFocus={() => nameSuggestions.length > 0 && setShowNameSuggestions(true)}
              onBlur={() => setTimeout(() => setShowNameSuggestions(false), 200)}
              placeholder=""
              autoFocus
              required
              autoComplete="off"
              data-testid="walkin-form-name"
            />
            {showNameSuggestions && nameSuggestions.length > 0 && (
              <div
                className="absolute z-30 mt-1 w-full bg-white border border-[#D9DCC6] rounded-md shadow-md max-h-56 overflow-y-auto"
                data-testid="walkin-form-name-suggestions"
              >
                {nameSuggestions.map((s, i) => (
                  <button
                    type="button"
                    key={`${s.name}-${s.phone}-${i}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applySuggestion(s)}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-[#F1F4E5] flex items-center justify-between gap-2"
                    data-testid={`walkin-form-name-suggestion-${i}`}
                  >
                    <span className="truncate">{s.name || '—'}</span>
                    {s.phone && <span className="text-[10px] text-[#8F9485] shrink-0">— {s.phone}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="wf-phone" className="text-xs">Contact Number</Label>
              <Input
                id="wf-phone"
                type="tel"
                value={form.customer_phone}
                onChange={set('customer_phone')}
                placeholder=""
                data-testid="walkin-form-phone"
              />
            </div>
            <div>
              <Label htmlFor="wf-visit-date" className="text-xs">Visit Date</Label>
              <Input
                id="wf-visit-date"
                type="date"
                value={form.visit_date}
                onChange={set('visit_date')}
                data-testid="walkin-form-visit-date"
              />
              <p className="text-[10px] text-[#8F9485] mt-1">Today by default — edit if back-dating.</p>
            </div>
            <div>
              <Label htmlFor="wf-followup" className="text-xs">Follow-up Date</Label>
              <Input
                id="wf-followup"
                type="date"
                value={form.follow_up_date}
                onChange={set('follow_up_date')}
                data-testid="walkin-form-followup"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="wf-location" className="text-xs">Location</Label>
            <Input
              id="wf-location"
              value={form.location}
              onChange={set('location')}
              placeholder=""
              data-testid="walkin-form-location"
            />
          </div>

          <div>
            <Label htmlFor="wf-products" className="text-xs">Products Shown</Label>
            <Input
              id="wf-products"
              value={form.products_shown}
              onChange={set('products_shown')}
              placeholder=""
              data-testid="walkin-form-products"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="wf-arch" className="text-xs">Architect Name <span className="text-[#8F9485]">(optional)</span></Label>
              <Input
                id="wf-arch"
                value={form.architect_name}
                onChange={set('architect_name')}
                placeholder=""
                data-testid="walkin-form-architect"
              />
            </div>
            <div>
              <Label htmlFor="wf-carp" className="text-xs">Carpenter Name <span className="text-[#8F9485]">(optional)</span></Label>
              <Input
                id="wf-carp"
                value={form.carpenter_name}
                onChange={set('carpenter_name')}
                placeholder=""
                data-testid="walkin-form-carpenter"
              />
            </div>
          </div>

          {/* Multi-staff attribution — pick every staff member who jointly
              attended this customer so the WhatsApp follow-up reminder
              fans out to all of them, not just whoever logged the form. */}
          <div>
            <Label className="text-xs">Attended By <span className="text-[#8F9485]">(team)</span></Label>
            <div
              className="mt-1 min-h-[40px] border border-[#D9DCC6] rounded-md px-2 py-1.5 flex flex-wrap gap-1 items-center bg-white focus-within:border-[#556B2F] transition-colors"
              data-testid="walkin-form-attending-staff"
            >
              {attendingStaff.map((s) => (
                <span
                  key={s.user_id}
                  className="inline-flex items-center gap-1 bg-[#F1F4E5] text-[#384518] text-xs px-2 py-1 rounded-full border border-[#D9DCC6]"
                  data-testid={`attending-staff-chip-${s.user_id}`}
                >
                  {s.name || 'Unnamed'}
                  {s.user_id === currentUser?.id && (
                    <span className="text-[10px] text-[#8F9485]">(you)</span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeStaff(s.user_id)}
                    className="ml-0.5 text-[#8F9485] hover:text-red-600"
                    aria-label={`Remove ${s.name}`}
                    data-testid={`attending-staff-remove-${s.user_id}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <input
                value={staffQuery}
                onChange={(e) => { setStaffQuery(e.target.value); setShowStaffDropdown(true); }}
                onFocus={() => setShowStaffDropdown(true)}
                onBlur={() => setTimeout(() => setShowStaffDropdown(false), 150)}
                placeholder={attendingStaff.length === 0 ? 'Add staff who attended…' : 'Add more…'}
                className="flex-1 min-w-[120px] text-xs outline-none bg-transparent"
                data-testid="walkin-form-attending-staff-input"
              />
            </div>
            {showStaffDropdown && filteredStaff.length > 0 && (
              <div className="relative">
                <div
                  className="absolute z-20 mt-1 w-full bg-white border border-[#D9DCC6] rounded-md shadow-md max-h-48 overflow-y-auto"
                  data-testid="walkin-form-staff-dropdown"
                >
                  {filteredStaff.map((s) => (
                    <button
                      type="button"
                      key={s.id}
                      onClick={() => addStaff(s)}
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-[#F1F4E5] flex items-center justify-between"
                      data-testid={`walkin-form-staff-option-${s.id}`}
                    >
                      <span>{s.name || 'Unnamed'} <span className="text-[#8F9485]">({s.role})</span></span>
                      {s.staff_code && <span className="text-[10px] text-[#8F9485]">{s.staff_code}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <p className="text-[10px] text-[#8F9485] mt-1">
              Each tagged staff will get a WhatsApp reminder on the follow-up date.
            </p>
          </div>

          <div>
            <Label htmlFor="wf-notes" className="text-xs">What did you discuss?</Label>
            <Textarea
              id="wf-notes"
              value={form.notes}
              onChange={set('notes')}
              placeholder=""
              rows={3}
              data-testid="walkin-form-notes"
            />
          </div>
        </form>

        <DialogFooter className="flex-shrink-0 pt-3 border-t border-[#E2E6D9] gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="walkin-form-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            className="bg-[#556B2F] hover:bg-[#465926] text-white"
            disabled={submitting}
            data-testid="walkin-form-submit"
          >
            {submitting
              ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> {isEdit ? 'Saving…' : 'Saving…'}</>
              : (isEdit ? 'Save Changes' : 'Save Walk-in')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
